import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Badge } from './ui/badge.jsx';
import { Button } from './ui/button.jsx';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Brush, Area, AreaChart } from 'recharts';
import { Zap, Gauge, TrendingDown, RefreshCw, Layers, Activity, ArrowDown, Sparkles, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import ResourceUtilizationGraph from './ResourceUtilizationGraph';

function OptimizationVisualization({ clusterId, recommendations }) {
  const [impactData, setImpactData] = useState([]);
  const [binPack, setBinPack] = useState({ recommendations: [], count: 0 });
  const [selectedKey, setSelectedKey] = useState(null);
  const [workloadViz, setWorkloadViz] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clusterId) return;
    loadClusterImpact();
    loadBinPackingRecs();
  }, [clusterId]);

  useEffect(() => {
    if (!selectedKey || !clusterId) {
      setWorkloadViz(null);
      return;
    }
    const [namespace, workload_name] = selectedKey.split('/')
    loadWorkloadViz(namespace, workload_name);
  }, [selectedKey, clusterId]);

  const loadClusterImpact = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/rightsizing-impact/${clusterId}`);
      setImpactData(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadBinPackingRecs = async () => {
    try {
      const res = await axios.get(`${API}/rightsizing/bin-packing/${clusterId}`);
      setBinPack({
        recommendations: res.data?.recommendations || [],
        count: res.data?.count || 0
      });
    } catch (e) {
      console.error(e);
    }
  };

  const loadWorkloadViz = async (namespace, workload_name) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rightsizing/visualization/${clusterId}/${namespace}/${workload_name}`);
      setWorkloadViz(res.data || null);
    } catch (e) {
      toast.error('Failed to load visualization metrics');
    } finally {
      setLoading(false);
    }
  };

  const workloadOptions = useMemo(() => {
    const items = (recommendations || []).map(r => ({
      key: `${r.namespace || 'default'}/${r.workload_name}`,
      label: `${r.namespace || 'default'}/${r.workload_name}`
    }));
    return items;
  }, [recommendations]);

  const avgPackingEfficiency = useMemo(() => {
    const list = binPack.recommendations || [];
    if (!list.length) return 0;
    const sum = list.reduce((acc, r) => acc + (r.packing_efficiency || 0), 0);
    return Math.round((sum / list.length) * 1000) / 10;
  }, [binPack]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 backdrop-blur-lg text-white rounded-xl p-4 text-sm shadow-2xl border border-slate-700">
          <div className="font-semibold mb-2 text-slate-200">{label}</div>
          {payload.map((p, idx) => (
            <div key={idx} className="flex items-center justify-between gap-4 py-1">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                {p.name}
              </span>
              <span className="font-mono font-medium">{typeof p.value === 'number' ? p.value.toFixed(2) : p.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-6 text-white shadow-xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <BarChart3 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Optimization Analytics</h2>
                <p className="text-white/80 text-sm mt-1">Real-time insights into your cluster optimization</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Select value={selectedKey || ''} onValueChange={setSelectedKey}>
                <SelectTrigger className="w-64 bg-white/10 border-white/20 text-white backdrop-blur-sm">
                  <SelectValue placeholder="Select workload to analyze" />
                </SelectTrigger>
                <SelectContent>
                  {workloadOptions.map(opt => (
                    <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={() => { loadClusterImpact(); if (selectedKey) { const [ns, name] = selectedKey.split('/'); loadWorkloadViz(ns, name); } }}
                className="bg-white/20 hover:bg-white/30 border-0 backdrop-blur-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Layers className="w-4 h-4" />
                Bin Pack Recs
              </div>
              <div className="text-3xl font-bold mt-1">{binPack.count}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Gauge className="w-4 h-4" />
                Packing Efficiency
              </div>
              <div className="text-3xl font-bold mt-1">{avgPackingEfficiency}%</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <TrendingDown className="w-4 h-4" />
                Efficiency Delta
              </div>
              <div className="text-3xl font-bold mt-1">
                {workloadViz?.efficiency_delta !== null && workloadViz?.efficiency_delta !== undefined
                  ? `${Math.round(workloadViz.efficiency_delta * 100)}%` : '--'}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Sparkles className="w-4 h-4" />
                Latest Savings
              </div>
              <div className="text-3xl font-bold mt-1">
                ${impactData?.length ? (impactData[impactData.length - 1].savings || 0).toFixed(0) : 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Comparison Chart */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-100">
            <CardTitle className="flex items-center text-lg">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              Cost: Actual vs Optimized
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={impactData}>
                  <defs>
                    <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="optimizedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Area type="monotone" dataKey="actual_cost" name="Actual Cost" stroke="#3b82f6" strokeWidth={2} fill="url(#actualGradient)" />
                  <Area type="monotone" dataKey="optimized_cost" name="Optimized Cost" stroke="#10b981" strokeWidth={2} fill="url(#optimizedGradient)" />
                  <Brush travellerWidth={10} height={25} stroke="#94a3b8" fill="#f8fafc" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Workload Comparison Chart */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-slate-100">
            <CardTitle className="flex items-center text-lg">
              <div className="p-2 bg-amber-100 rounded-lg mr-3">
                <ArrowDown className="w-5 h-5 text-amber-600" />
              </div>
              Workload: Before vs After
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div style={{ height: 280 }}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                    <span className="text-slate-500">Loading visualization...</span>
                  </div>
                </div>
              ) : workloadViz ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { label: 'CPU Request', before: workloadViz.pre.avg_cpu_request, after: workloadViz.post.avg_cpu_request },
                    { label: 'CPU Usage', before: workloadViz.pre.avg_cpu_usage, after: workloadViz.post.avg_cpu_usage },
                    { label: 'Memory (MB)', before: workloadViz.pre.avg_memory_request, after: workloadViz.post.avg_memory_request },
                    { label: 'Mem Usage', before: workloadViz.pre.avg_memory_usage, after: workloadViz.post.avg_memory_usage },
                  ]} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar dataKey="before" name="Before" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="after" name="After" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Select a workload to visualize</p>
                    <p className="text-slate-400 text-sm">Compare before/after optimization metrics</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Resource Utilization Graph */}
      <ResourceUtilizationGraph clusterId={clusterId} />

      {/* Footer Hint */}
      <div className="text-center text-sm text-slate-500 py-2">
        <Sparkles className="w-4 h-4 inline mr-2" />
        Use the brush on charts to zoom into specific time periods
      </div>
    </div>
  );
}

export default OptimizationVisualization;